import React, { useEffect, useRef, useState } from "react";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import { Button } from "react-bootstrap";
import { UserDto } from "../../../../../../interfaces/team/RegionHierarchy";
import { getAllUsersWithSameOrganizationandRegionForSharingFile } from "../../../../../../services/userServices/TeamService";
import { toast } from "react-toastify";
import Loader from "../../../../loader/Loader";
import { DriveItem } from "../../../../../../interfaces/caseFileDrive/DriveFiles";
import { SharedDriveExistingFlle } from "../../../../../../services/fileServices/FileService";
import { useAuth } from "../../../../../../firebase/AuthProvider";
import "./UploadedFileShareModal.css";

interface UploadedFileShareModalProps {
  open: boolean;
  onClose: () => void;
  authToken: string;
  selectedFile: DriveItem | null | any;
  trigger?: JSX.Element;
  refreshCurrectFolder: () => void;
}

const UploadedFileShareModal = ({
  open,
  onClose,
  authToken,
  selectedFile,
  trigger,
  refreshCurrectFolder,
}: UploadedFileShareModalProps) => {
  const { currentUser } = useAuth();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [allUsers, setAllUsers] = useState<UserDto[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [formErrors, setFormErrors] = useState<{
    selectedUserIds?: string;
  }>({});

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  function getHighestRole(roles: any) {
    if (!Array.isArray(roles) || roles.length === 0) {
      return null;
    }

    const rolePriority = [
      "ADMIN",
      "OWNER",
      "PROGRAM MANAGER",
      "SUPERVISOR",
      "SC",
      "ASSESSOR",
    ];

    const roleLabelMap: any = {
      ADMIN: "Admin",
      OWNER: "Owner",
      SUPERVISOR: "Supervisor",
      "PROGRAM MANAGER": "Program manager",
      SC: "Support coordinator",
      ASSESSOR: "Assessor",
    };

    const normalizedRoles = roles
      .filter(Boolean)
      .map((role) => role.toUpperCase());

    // 1️⃣ Check for highest priority role
    const matchedRole = rolePriority.find((role) =>
      normalizedRoles.includes(role)
    );

    if (matchedRole) {
      return roleLabelMap[matchedRole];
    }

    // 2️⃣ Fallback: role not in priority list (lowest priority)
    const fallbackRole = roles[0];

    return fallbackRole
      .toLowerCase()
      .replace(/\b\w/g, (char: string) => char.toUpperCase());
  }

  useEffect(() => {
    if (open && selectedFile) {
      fetchAllUser();
    }
  }, [open, selectedFile]);

  const fetchAllUser = async () => {
    if (!selectedFile) return;

    try {
      setLoading(true);
      const response =
        await getAllUsersWithSameOrganizationandRegionForSharingFile(
          authToken,
          {
            searchTerm: null,
            sortBy: null,
            sortDescending: true,
            pageNumber: 1,
            pageSize: 1000,
            fileId: selectedFile.id,
          }
        );
      if (response) {
        setAllUsers(response.data);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (id: string) => {
    setSelectedUserIds((prev) => {
      const isSelected = prev.includes(id);
      return isSelected
        ? prev.filter((userId) => userId !== id)
        : [...prev, id];
    });
    setFormErrors((prev) => ({ ...prev, selectedUserIds: undefined }));
  };

  const removeUser = (id: string) => {
    setSelectedUserIds((prev) => prev.filter((userId) => userId !== id));
  };

  const selectedUsers = allUsers.filter((u) => selectedUserIds.includes(u.id));

  const filteredUsers = allUsers.filter((user) => {
    if (user.id === currentUser?.uid) return false;
    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase());
  });

  const handleclickShare = async () => {
    if (!selectedFile) return;

    const newErrors: typeof formErrors = {};
    if (selectedUserIds.length === 0) {
      newErrors.selectedUserIds = "Please select at least one user.";
    }
    if (Object.keys(newErrors).length > 0) {
      toast.error("Please fill in all required fields.");
      setFormErrors(newErrors);
      return;
    }
    if (!currentUser) return;

    setLoading(true);
    SharedDriveExistingFlle(
      selectedFile.id,
      authToken,
      currentUser.uid,
      selectedUserIds
    )
      .then((res) => {
        if (res.success) {
          toast.success("File Shared Successfully");
          refreshCurrectFolder();
          resetState();
          onClose();
        } else {
          toast.error(res.message);
        }
      })
      .catch((err) => {
        toast.error(err.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const resetState = () => {
    setSelectedUserIds([]);
    setSearchTerm("");
    setShowDropdown(false);
    setFormErrors({});
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  // Don't render if no file selected
  if (!selectedFile) {
    return null;
  }

  return (
    <>
      <Popup
        open={open}
        trigger={trigger}
        onClose={handleClose}
        closeOnDocumentClick
        arrow={false}
        position={[
          "bottom left",
          "bottom right",
          "top left",
          "top right",
          "bottom center",
          "top center",
        ]}
        keepTooltipInside
        contentStyle={{
          width: "500px",
          maxWidth: "90vw",
          maxHeight: "80vh",
          borderRadius: "8px",
          padding: "0",
          border: "none",
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
        }}
      >
        <div
          className="custom_popup1"
          style={{
            display: "flex",
            flexDirection: "column",
            maxHeight: "80vh",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "16px 20px",
              borderBottom: "1px solid #dee2e6",
              flexShrink: 0,
            }}
          >
            <h5 className="share-title" style={{ margin: 0 }}>
              Share view access
            </h5>
          </div>

          {/* Body - Scrollable */}
          <div
            style={{
              padding: "20px",
            }}
          >
            <div className="share-access-wrapper" ref={dropdownRef}>
              <div className="share-access-header">
                <div className="share-search-box">
                  <svg
                    className="share-search-icon"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                  >
                    <path
                      d="M6.33333 11C8.72667 11 10.6667 9.06 10.6667 6.66667C10.6667 4.27333 8.72667 2.33333 6.33333 2.33333C3.94 2.33333 2 4.27333 2 6.66667C2 9.06 3.94 11 6.33333 11Z"
                      stroke="#9CA3AF"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M12 12L9.4 9.4"
                      stroke="#9CA3AF"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <input
                    type="text"
                    className="share-search-input"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onFocus={() => setShowDropdown(true)}
                  />
                </div>
                <button
                  type="button"
                  className="share-add-btn"
                  onClick={() => setShowDropdown(!showDropdown)}
                >
                  Add
                </button>
              </div>

              {showDropdown && (
                <div className="share-dropdown-menu">
                  {loading ? (
                    <div className="share-dropdown-empty">Loading...</div>
                  ) : filteredUsers.length === 0 ? (
                    <div className="share-dropdown-empty">No users found</div>
                  ) : (
                    filteredUsers.map((user) => (
                      <div
                        key={user.id}
                        className="share-dropdown-option"
                        onClick={() => {
                          toggleSelection(user.id);
                          setSearchTerm("");
                          setShowDropdown(false);
                        }}
                      >
                        <div className="share-user-avatar">
                          {user.firstName.charAt(0)}
                          {user.lastName.charAt(0)}
                        </div>
                        <div className="share-user-info">
                          <div className="share-user-name">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="share-user-role">
                            {getHighestRole(user?.roles)}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {selectedUsers.length > 0 && (
                <div className="share-selected-list">
                  {selectedUsers.map((user) => (
                    <div key={user.id} className="share-selected-item">
                      <div className="share-user-avatar">
                        {user.firstName.charAt(0)}
                        {user.lastName.charAt(0)}
                      </div>
                      <div className="share-user-info">
                        <div className="share-user-name">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="share-user-role">Supervisor</div>
                      </div>
                      <button
                        type="button"
                        className="share-remove-btn"
                        onClick={() => removeUser(user.id)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {formErrors.selectedUserIds && (
                <div className="share-error-text">
                  {formErrors.selectedUserIds}
                </div>
              )}
            </div>
          </div>

          {/* Footer - Fixed */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
              padding: "16px 20px",
              borderTop: "1px solid #dee2e6",
              flexShrink: 0,
            }}
          >
            <Button
              className="btn btn-secondary cancel-btn"
              onClick={handleClose}
            >
              Close
            </Button>
            <Button
              className="btn btn-primary submit-btn"
              onClick={handleclickShare}
              disabled={loading}
            >
              Share File
            </Button>
          </div>
        </div>
      </Popup>
    </>
  );
};

export default UploadedFileShareModal;
